import { useSelector } from 'react-redux'

import type { UserStateRequired } from 'types/user'

/**
 * Represents information about a business's watermarking quota usage.
 *
 * @property quotaUsed - Number of watermarked playlists used
 * @property quota - Total watermarked playlist quota allowed
 * @property overQuota - Whether the quota has been exceeded
 * @property quotaUnlimited - Whether the business has unlimited watermarking
 */
export type WatermarkingQuota = {
  quotaUsed: number
  quota?: number
  overQuota?: boolean
  quotaUnlimited?: boolean
}

/**
 * Magic number representing unlimited watermarking quota.
 * When quota equals or exceeds this value, watermarking is considered unlimited.
 */
export const UNLIMITED_WATERMARKING_QUOTA = 99999

/**
 * Calculates watermarking quota usage information based on the user state.
 *
 * Extracts watermarking quota and usage from business metadata and determines
 * if the business is over quota or has unlimited watermarking.
 *
 * @param state - The Redux user state containing business information
 * @returns Object containing quota information and usage status
 */
export const getWatermarkingQuota = (
  state: UserStateRequired,
): WatermarkingQuota => {
  const quota =
    state.user.business?.private_metadata?.watermarked_playlists_quota || 0
  const quotaUsed =
    state.user.business?.private_metadata?.watermarked_playlists_quota_used || 0

  return {
    quotaUsed,
    quota,
    // watermarked_playlists_quota=99999 means unlimited watermarking plan
    overQuota: quota < UNLIMITED_WATERMARKING_QUOTA && quotaUsed >= quota,
    quotaUnlimited: quota >= UNLIMITED_WATERMARKING_QUOTA,
  }
}

/**
 * React hook that provides access to watermarking quota information.
 *
 * Uses Redux selector pattern to retrieve current watermarking quota usage.
 *
 * @returns Current watermarking quota information
 */
export const useWatermarkingQuota = (): WatermarkingQuota =>
  useSelector(getWatermarkingQuota)
